<template>
  <div class="pagination-bar" v-if="totalItems > 0">
    <div class="pgc-left">
      <label class="pgc-rows-label">Rows per page</label>
      <select
        class="pgc-rows-select"
        :value="perPage"
        @change="$emit('perPage', Number($event.target.value))"
      >
        <option v-for="opt in perPageOptions" :key="opt" :value="opt">
          {{ opt }}
        </option>
      </select>
      <span class="pgc-info">{{ startIndex }}–{{ endIndex }} of {{ totalItems }}</span>
    </div>
    <div class="pgc-right">
      <button class="pgc-btn" :disabled="currentPage === 1" @click="go(1)" title="First page">
        <i class="fa fa-angle-double-left"></i>
      </button>
      <button class="pgc-btn" :disabled="currentPage === 1" @click="go(currentPage - 1)" title="Previous page">
        <i class="fa fa-angle-left"></i>
      </button>
      <button
        v-for="p in pageWindow"
        :key="p"
        class="pgc-num"
        :class="{ active: p === currentPage }"
        @click="go(p)"
      >
        {{ p }}
      </button>
      <button class="pgc-btn" :disabled="currentPage >= totalPages" @click="go(currentPage + 1)" title="Next page">
        <i class="fa fa-angle-right"></i>
      </button>
      <button class="pgc-btn" :disabled="currentPage >= totalPages" @click="go(totalPages)" title="Last page">
        <i class="fa fa-angle-double-right"></i>
      </button>
    </div>
  </div>
</template>

<script>
/**
 * Reusable full pagination bar: rows-per-page, "X–Y of Z", and
 * First / Prev / windowed numbers / Next / Last controls.
 *
 * Emits:
 *  - change(page)    when a page is selected
 *  - perPage(value)  when the rows-per-page changes
 */
export default {
  name: "Pagination",
  props: {
    currentPage: { type: Number, default: 1 },
    totalPages: { type: Number, default: 1 },
    totalItems: { type: Number, default: 0 },
    perPage: { type: Number, default: 10 },
    perPageOptions: { type: Array, default: () => [10, 25, 50, 100] },
  },
  emits: ["change", "perPage"],
  computed: {
    startIndex() {
      return this.totalItems === 0 ? 0 : (this.currentPage - 1) * this.perPage + 1;
    },
    endIndex() {
      return Math.min(this.currentPage * this.perPage, this.totalItems);
    },
    pageWindow() {
      const total = this.totalPages || 1;
      let start = Math.max(1, this.currentPage - 2);
      const end = Math.min(total, start + 4);
      start = Math.max(1, end - 4);
      const pages = [];
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },
  },
  methods: {
    go(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) return;
      this.$emit("change", page);
    },
  },
};
</script>

<style lang="scss" scoped>
.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.85rem 0.25rem 0.25rem;

  .pgc-left {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    font-size: 0.82rem;
    color: #6b7280;
  }
  .pgc-rows-label {
    white-space: nowrap;
  }
  .pgc-rows-select {
    height: 32px;
    padding: 0 0.5rem;
    border: 1px solid #e2e5ea;
    border-radius: 6px;
    background: #fff;
    color: #374151;
    font-size: 0.82rem;
    cursor: pointer;
  }
  .pgc-info {
    white-space: nowrap;
    margin-left: 0.25rem;
  }
  .pgc-right {
    display: flex;
    align-items: center;
    gap: 0.3rem;
  }
  .pgc-btn,
  .pgc-num {
    min-width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 0.5rem;
    font-size: 0.82rem;
    font-weight: 500;
    border: 1px solid #e2e5ea;
    background: #fff;
    color: #555;
    border-radius: 6px;
    cursor: pointer;
    &:hover:not(:disabled):not(.active) {
      border-color: #584dee;
      color: #584dee;
    }
    &:disabled {
      opacity: 0.4;
      cursor: default;
    }
  }
  .pgc-num.active {
    background: #584dee;
    border-color: #584dee;
    color: #fff;
    font-weight: 700;
  }
}
</style>
